/**
 * Map assistant chat `debugState` (SessionDebugState) into labeled panel rows.
 * Never includes secrets — backend snapshot is already redacted.
 */

export type VysorSessionSnapshot = {
  activeVysorGoal?: string | null;
  application?: string | null;
  environment?: string | null;
  protocol?: string | null;
  grantType?: string | null;
  clientId?: string | null;
  entityId?: string | null;
  acsUrl?: string | null;
  redirectUris?: string[] | null;
  oidcDiscovery?: string | null;
  nameId?: string | null;
  igaEntitlementName?: string | null;
  igaEntitlementDescription?: string | null;
  igaEntitlementType?: string | null;
  selectedAppId?: string | null;
  selectedInstanceId?: string | null;
  awaitingProtocol?: boolean;
  pendingIrreversible?: string | null;
  ssoDraftReady?: boolean;
  conversationalIntent?: string | null;
  intent?: string | null;
  missingRequired?: string[];
  pendingQuestionField?: string | null;
};

export type SnapshotRow = {
  key: string;
  label: string;
  value: string;
};

export type SnapshotGroup = {
  id: string;
  title: string;
  rows: SnapshotRow[];
};

function text(v?: string | null): string | null {
  const s = String(v || '').trim();
  return s || null;
}

function goalLabel(goal?: string | null): string | null {
  const g = text(goal);
  if (!g) return null;
  if (g === 'configure-sso') return 'Configure SSO';
  if (g === 'setup-app') return 'Setup Application';
  if (g === 'iga-entitlements') return 'IGA entitlements';
  return g;
}

/** Build display groups from a session snapshot. Empty groups are omitted. */
export function buildSnapshotGroups(snapshot: VysorSessionSnapshot | null | undefined): SnapshotGroup[] {
  if (!snapshot) return [];

  const groups: SnapshotGroup[] = [];

  const workflow: SnapshotRow[] = [];
  const goal = goalLabel(snapshot.activeVysorGoal);
  if (goal) workflow.push({ key: 'goal', label: 'Goal', value: goal });
  if (text(snapshot.pendingQuestionField)) {
    workflow.push({
      key: 'pending',
      label: 'Waiting on',
      value: String(snapshot.pendingQuestionField),
    });
  }
  if (snapshot.awaitingProtocol) {
    workflow.push({ key: 'awaitProtocol', label: 'Awaiting', value: 'Protocol choice' });
  }
  if (snapshot.ssoDraftReady) {
    workflow.push({ key: 'draftReady', label: 'SSO draft', value: 'Ready to submit' });
  }
  if (text(snapshot.intent) || text(snapshot.conversationalIntent)) {
    workflow.push({
      key: 'intent',
      label: 'Intent',
      value: text(snapshot.intent) || text(snapshot.conversationalIntent) || '',
    });
  }
  if (Array.isArray(snapshot.missingRequired) && snapshot.missingRequired.length) {
    workflow.push({
      key: 'missing',
      label: 'Still needed',
      value: snapshot.missingRequired.join(', '),
    });
  }
  if (workflow.length) groups.push({ id: 'workflow', title: 'Workflow', rows: workflow });

  const app: SnapshotRow[] = [];
  if (text(snapshot.application)) {
    app.push({ key: 'application', label: 'Application', value: String(snapshot.application) });
  }
  if (text(snapshot.environment)) {
    app.push({ key: 'environment', label: 'Environment', value: String(snapshot.environment) });
  }
  if (app.length) groups.push({ id: 'app', title: 'Application', rows: app });

  const sso: SnapshotRow[] = [];
  if (text(snapshot.protocol)) {
    sso.push({ key: 'protocol', label: 'Protocol', value: String(snapshot.protocol) });
  }
  if (text(snapshot.grantType)) {
    sso.push({ key: 'grant', label: 'Grant type', value: String(snapshot.grantType) });
  }
  if (text(snapshot.clientId)) {
    sso.push({ key: 'clientId', label: 'Client ID', value: String(snapshot.clientId) });
  }
  if (text(snapshot.entityId)) {
    sso.push({ key: 'entityId', label: 'Entity ID', value: String(snapshot.entityId) });
  }
  if (text(snapshot.acsUrl)) {
    sso.push({ key: 'acs', label: 'ACS URL', value: String(snapshot.acsUrl) });
  }
  if (Array.isArray(snapshot.redirectUris) && snapshot.redirectUris.length) {
    sso.push({
      key: 'redirects',
      label: 'Redirect URIs',
      value: snapshot.redirectUris.filter(Boolean).join('\n'),
    });
  }
  if (text(snapshot.oidcDiscovery)) {
    sso.push({ key: 'discovery', label: 'OIDC discovery', value: String(snapshot.oidcDiscovery) });
  }
  if (text(snapshot.nameId)) {
    sso.push({ key: 'nameId', label: 'Name ID', value: String(snapshot.nameId) });
  }
  if (sso.length) groups.push({ id: 'sso', title: 'SSO', rows: sso });

  const iga: SnapshotRow[] = [];
  if (text(snapshot.igaEntitlementName)) {
    iga.push({ key: 'igaName', label: 'Entitlement', value: String(snapshot.igaEntitlementName) });
  }
  if (text(snapshot.igaEntitlementType)) {
    iga.push({ key: 'igaType', label: 'Type', value: String(snapshot.igaEntitlementType) });
  }
  if (text(snapshot.igaEntitlementDescription)) {
    iga.push({
      key: 'igaDesc',
      label: 'Description',
      value: String(snapshot.igaEntitlementDescription),
    });
  }
  if (iga.length) groups.push({ id: 'iga', title: 'IGA', rows: iga });

  return groups;
}

export function snapshotHasValues(snapshot: VysorSessionSnapshot | null | undefined): boolean {
  return buildSnapshotGroups(snapshot).length > 0;
}
